import React, { useEffect, useState } from "react";
import api from "../../utils/axios";
import Swal from "sweetalert2";
import {
  Mail,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  RefreshCw,
  Calendar,
  User,
  ShieldCheck,
  AlertTriangle,
  Award,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText
} from "lucide-react";

interface AmoeEntry {
  id: number;
  code: string;
  entry_reference: string;
  user_id: number;
  status: string;
  reward_amount: string | number;
  reward_credited: boolean;
  reward_credited_at: string | null;
  reward_transaction_id: string | null;
  created_at: string;
  expires_at: string;
  postcard_received_at: string | null;
  reviewed_at: string | null;
  reviewed_by: number | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  user_name?: string;
  user_email?: string;
  user_phone?: string;
  user_state?: string;
  user_country?: string;
  kyc_status?: string;
  kyc_name?: string;
  reviewer_name?: string;
  audit_logs?: AuditLog[];
}

interface AuditLog {
  id: number;
  amoe_id: number;
  action: string;
  previous_status: string | null;
  new_status: string | null;
  performed_by_type: string;
  performed_by_id: number | null;
  reason: string | null;
  notes: string | null;
  created_at: string;
  admin_username?: string;
}

interface AnalyticsData {
  totalCodes: number;
  postcardsReceived: number;
  awaitingReview: number;
  approvedEntries: number;
  rejectedEntries: number;
  pendingMailEntries: number;
  expiredEntries: number;
  totalRewardsCredited: number;
}

const REJECTION_REASONS = [
  "Illegible or incomplete handwriting",
  "Mailed from outside registered residential state",
  "Missing or invalid registered account email/name",
  "Code expired or invalid",
  "Not enclosed in a stamped #10 envelope",
  "Card does not measure 4\" x 6\" or unlined paper requirement not met",
  "Multiple request cards enclosed in single outer envelope",
  "Mechanically reproduced, photocopied, or printed request card",
  "Missing mandatory sweepstakes agreement statement",
  "Other compliance or verification failure"
];

const AmoeManagement: React.FC = () => {
  const [entries, setEntries] = useState<AmoeEntry[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalCodes: 0,
    postcardsReceived: 0,
    awaitingReview: 0,
    approvedEntries: 0,
    rejectedEntries: 0,
    pendingMailEntries: 0,
    expiredEntries: 0,
    totalRewardsCredited: 0
  });

  const [loading, setLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(15);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Modal / Review Drawer
  const [selectedEntry, setSelectedEntry] = useState<AmoeEntry | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReasonSelect, setRejectReasonSelect] = useState(REJECTION_REASONS[0]);
  const [customRejectReason, setCustomRejectReason] = useState("");

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch Analytics
  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const res = await api.get("/amoe/analytics");
      if (res.data?.status) {
        setAnalytics(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching AMOE analytics:", err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Fetch Entries
  const fetchEntries = async () => {
    setLoading(true);
    try {
      const res = await api.get("/amoe/entries", {
        params: {
          page,
          limit,
          status: statusFilter || undefined,
          search: debouncedSearch || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined
        }
      });

      if (res.data?.status) {
        setEntries(res.data.data || []);
        setTotal(res.data.total || 0);
        setTotalPages(res.data.totalPages || 1);
      }
    } catch (err) {
      console.error("Error fetching AMOE entries:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [page, debouncedSearch, statusFilter, startDate, endDate]);

  // Fetch details when opening modal
  const handleOpenDetails = async (entry: AmoeEntry) => {
    setSelectedEntry(entry);
    try {
      const res = await api.get(`/amoe/entries/${entry.id}`);
      if (res.data?.status) {
        setSelectedEntry(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching detailed entry info:", err);
    }
  };

  // Status Action Handler
  const handleUpdateStatus = async (
    targetStatus: "postcard_received" | "under_review" | "approved" | "rejected",
    reason?: string
  ) => {
    if (!selectedEntry) return;

    // Confirmations
    if (targetStatus === "approved") {
      const confirmResult = await Swal.fire({
        title: "Approve AMOE Entry?",
        html: `
          <div style="text-align: left; font-size: 14px;">
            <p><strong>Customer:</strong> ${selectedEntry.user_name || "UID " + selectedEntry.user_id}</p>
            <p><strong>Code:</strong> ${selectedEntry.code}</p>
            <p style="margin-top: 10px; color: #15803d; font-weight: bold;">
              ✓ This action will credit <strong>${parseFloat(String(selectedEntry.reward_amount || 2)).toFixed(2)} Sweeps Coins</strong> directly into the player's wallet balance.
            </p>
            <p style="color: #6b7280; font-size: 12px; margin-top: 8px;">
              This operation is transactional and idempotent. Once approved, it cannot be undone.
            </p>
          </div>
        `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Approve & Credit Reward",
        confirmButtonColor: "#10b981",
        cancelButtonText: "Cancel"
      });

      if (!confirmResult.isConfirmed) return;
    }

    if (targetStatus === "postcard_received") {
      const confirmResult = await Swal.fire({
        title: "Record Postcard Receipt?",
        text: `Mark physical postcard for code ${selectedEntry.code} as received at the Berwick, PA processing center?`,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Yes, Mark Received",
        confirmButtonColor: "#3b82f6",
        cancelButtonText: "Cancel"
      });

      if (!confirmResult.isConfirmed) return;
    }

    setActionLoading(true);
    try {
      const res = await api.patch(`/amoe/entries/${selectedEntry.id}/status`, {
        status: targetStatus,
        rejection_reason: reason || undefined
      });

      if (res.data?.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: res.data.message || "Entry updated successfully.",
          timer: 2500,
          showConfirmButton: false
        });

        setShowRejectModal(false);
        setCustomRejectReason("");
        // Reload details & lists
        fetchEntries();
        fetchAnalytics();
        handleOpenDetails({ ...selectedEntry, status: targetStatus });
      }
    } catch (err: any) {
      console.error("Status update error:", err);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: err.response?.data?.message || err.message || "Failed to update entry status."
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Submit Rejection
  const handleSubmitRejection = () => {
    const finalReason = customRejectReason.trim() || rejectReasonSelect;
    if (!finalReason) {
      Swal.fire({
        icon: "warning",
        title: "Reason Required",
        text: "Please select or type a rejection reason."
      });
      return;
    }
    handleUpdateStatus("rejected", finalReason);
  };

  // Status Badge Helper
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "code_generated":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
            <Clock size={12} /> Code Generated
          </span>
        );
      case "postcard_received":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
            <Mail size={12} /> Postcard Received
          </span>
        );
      case "under_review":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200">
            <Clock size={12} /> Under Review
          </span>
        );
      case "approved":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle size={12} /> Approved (+2 SC)
          </span>
        );
      case "rejected":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <XCircle size={12} /> Rejected
          </span>
        );
      case "expired":
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200">
            <Clock size={12} /> Expired
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
            {status}
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* ── HEADER ──────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2.5">
            <Mail className="text-indigo-600" size={28} />
            AMOE Postal Entry Management
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Sweepstakes Alternative Method of Entry: review physical postcards, verify eligibility, and process 2.00 SC reward credits.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              fetchAnalytics();
              fetchEntries();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-700 hover:bg-slate-50 transition shadow-sm text-sm font-medium"
          >
            <RefreshCw size={16} className={loading || analyticsLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* ── METRIC OVERVIEW CARDS ────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Codes */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
            Total Codes Generated
          </div>
          <div className="text-2xl font-bold text-slate-800 mt-1.5">
            {analytics.totalCodes.toLocaleString()}
          </div>
          <div className="text-xs text-slate-500 mt-1 flex items-center gap-1">
            <Clock size={12} />
            <span>{analytics.pendingMailEntries} awaiting physical mail</span>
          </div>
        </div>

        {/* Postcards Received */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="text-xs font-semibold text-amber-600 uppercase tracking-wider">
            Postcards Received
          </div>
          <div className="text-2xl font-bold text-amber-700 mt-1.5">
            {analytics.postcardsReceived.toLocaleString()}
          </div>
          <div className="text-xs text-amber-600/80 mt-1">
            Recorded at processing center
          </div>
        </div>

        {/* Under Review */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="text-xs font-semibold text-purple-600 uppercase tracking-wider">
            Awaiting Review
          </div>
          <div className="text-2xl font-bold text-purple-700 mt-1.5">
            {analytics.awaitingReview.toLocaleString()}
          </div>
          <div className="text-xs text-purple-600/80 mt-1">
            Verification in progress
          </div>
        </div>

        {/* Approved */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">
            Approved Entries
          </div>
          <div className="text-2xl font-bold text-emerald-700 mt-1.5">
            {analytics.approvedEntries.toLocaleString()}
          </div>
          <div className="text-xs text-emerald-600/80 mt-1">
            ${analytics.totalRewardsCredited.toFixed(2)} SC awarded
          </div>
        </div>

        {/* Rejected */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="text-xs font-semibold text-rose-600 uppercase tracking-wider">
            Rejected Entries
          </div>
          <div className="text-2xl font-bold text-rose-700 mt-1.5">
            {analytics.rejectedEntries.toLocaleString()}
          </div>
          <div className="text-xs text-rose-600/80 mt-1">
            Non-compliant submissions
          </div>
        </div>
      </div>

      {/* ── FILTER & SEARCH BAR ──────────────────────────────────── */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          {/* Search */}
          <div className="md:col-span-5 relative">
            <Search className="absolute left-3 top-2.5 text-slate-400" size={18} />
            <input
              type="text"
              placeholder="Search by Code, Reference, UID, Name, or Email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-sm"
            />
          </div>

          {/* Status Filter */}
          <div className="md:col-span-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="w-full py-2 px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-sm bg-white"
            >
              <option value="">All Statuses</option>
              <option value="code_generated">Code Generated (Pending Mail)</option>
              <option value="postcard_received">Postcard Received</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="expired">Expired</option>
            </select>
          </div>

          {/* Date Range Start */}
          <div className="md:col-span-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              className="w-full py-2 px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-sm"
            />
          </div>

          {/* Date Range End */}
          <div className="md:col-span-2">
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              className="w-full py-2 px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-sm"
            />
          </div>
        </div>

        {/* Clear Filters Button if active */}
        {(search || statusFilter || startDate || endDate) && (
          <div className="flex justify-end">
            <button
              onClick={() => {
                setSearch("");
                setStatusFilter("");
                setStartDate("");
                setEndDate("");
                setPage(1);
              }}
              className="text-xs text-slate-500 hover:text-slate-800 underline"
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* ── ENTRIES TABLE ────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex justify-between items-center bg-slate-50/50">
          <div>
            <h2 className="text-base font-bold text-slate-800">
              AMOE Entry Registry ({total})
            </h2>
            <p className="text-xs text-slate-400 mt-0.5">
              Review and record physical postcard receipt and audit decisions
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left text-slate-700">
            <thead className="bg-slate-100 text-slate-600 uppercase text-xs font-bold border-b">
              <tr>
                <th className="px-5 py-4">Reference & Code</th>
                <th className="px-5 py-4">Customer Details</th>
                <th className="px-5 py-4">Generated Date</th>
                <th className="px-5 py-4">Postcard Received</th>
                <th className="px-5 py-4">Status</th>
                <th className="px-5 py-4">Review Details</th>
                <th className="px-5 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                    <RefreshCw className="animate-spin inline-block mr-2" size={18} />
                    Loading AMOE entries...
                  </td>
                </tr>
              ) : entries.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-slate-400">
                    No AMOE entries found matching criteria.
                  </td>
                </tr>
              ) : (
                entries.map((entry) => (
                  <tr key={entry.id} className="hover:bg-slate-50 transition">
                    {/* Code & Reference */}
                    <td className="px-5 py-4">
                      <div className="font-mono font-bold text-slate-900 text-sm">
                        {entry.code}
                      </div>
                      <div className="font-mono text-xs text-slate-400 mt-0.5">
                        {entry.entry_reference}
                      </div>
                    </td>

                    {/* Customer Info */}
                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-800">
                        {entry.user_name || "UID: " + entry.user_id}
                      </div>
                      <div className="text-xs text-slate-500">
                        {entry.user_email}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        UID: {entry.user_id} • {entry.user_state || "Unknown State"}
                      </div>
                    </td>

                    {/* Generated Date */}
                    <td className="px-5 py-4 text-xs text-slate-600">
                      <div>{new Date(entry.created_at).toLocaleDateString()}</div>
                      <div className="text-slate-400">
                        Expires: {new Date(entry.expires_at).toLocaleDateString()}
                      </div>
                    </td>

                    {/* Postcard Received Date */}
                    <td className="px-5 py-4 text-xs">
                      {entry.postcard_received_at ? (
                        <div className="text-emerald-700 font-medium">
                          {new Date(entry.postcard_received_at).toLocaleDateString()}
                        </div>
                      ) : (
                        <span className="text-slate-400 italic">Not received yet</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4">
                      {renderStatusBadge(entry.status)}
                    </td>

                    {/* Review Info */}
                    <td className="px-5 py-4 text-xs text-slate-500">
                      {entry.reviewed_at ? (
                        <div>
                          <div>{new Date(entry.reviewed_at).toLocaleDateString()}</div>
                          <div className="text-slate-400">
                            By: {entry.reviewer_name || "Admin"}
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => handleOpenDetails(entry)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg hover:bg-black transition text-xs font-semibold shadow-sm"
                      >
                        <Eye size={14} />
                        Review & Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
            <span>
              Page {page} of {totalPages} ({total} entries)
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1.5 border rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 border rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── ENTRY REVIEW MODAL / DRAWER ─────────────────────────── */}
      {selectedEntry && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl relative my-8 border border-slate-200">
            <div className="flex items-center justify-between pb-4 border-b border-slate-200">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-indigo-50 text-indigo-600 rounded-xl">
                  <Mail size={22} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">
                    AMOE Entry: <span className="font-mono text-indigo-600">{selectedEntry.code}</span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    Reference: {selectedEntry.entry_reference}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedEntry(null)}
                className="text-slate-400 hover:text-slate-600 text-xl font-bold cursor-pointer"
              >
                &times;
              </button>
            </div>

            <div className="py-4 space-y-6 max-h-[70vh] overflow-y-auto pr-1">
              {/* Status and quick summary banner */}
              <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500 uppercase font-semibold">
                    Current Entry Status
                  </div>
                  <div className="mt-1">
                    {renderStatusBadge(selectedEntry.status)}
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-xs text-slate-500 uppercase font-semibold">
                    Reward Allocation
                  </div>
                  <div className="text-sm font-bold text-emerald-600 mt-1">
                    ${parseFloat(String(selectedEntry.reward_amount || 2)).toFixed(2)} Sweeps Coins
                  </div>
                  {selectedEntry.reward_credited && (
                    <span className="text-[11px] text-emerald-700 font-medium">
                      ✓ Credited to player wallet
                    </span>
                  )}
                </div>
              </div>

              {/* Customer Verification Profile */}
              <div>
                <h4 className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-2 flex items-center gap-1.5">
                  <User size={14} /> Customer Profile Verification
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 bg-slate-50 rounded-xl border text-xs">
                  <div>
                    <span className="text-slate-400 block">Registered Name:</span>
                    <span className="font-semibold text-slate-800">{selectedEntry.user_name || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Account Email:</span>
                    <span className="font-semibold text-slate-800">{selectedEntry.user_email || "N/A"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Customer UID:</span>
                    <span className="font-semibold text-slate-800">{selectedEntry.user_id}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Registered State:</span>
                    <span className="font-semibold text-slate-800">{selectedEntry.user_state || "Not provided"}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">KYC Verification:</span>
                    <span className={`font-semibold ${selectedEntry.kyc_status === "verified" ? "text-emerald-600" : "text-amber-600"}`}>
                      {selectedEntry.kyc_status || "Standard"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Reward Credited Ref:</span>
                    <span className="font-mono text-slate-600 text-[11px]">
                      {selectedEntry.reward_transaction_id || "None"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Timestamp Audit */}
              <div>
                <h4 className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-2 flex items-center gap-1.5">
                  <Calendar size={14} /> Submission & Review Timestamps
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-4 bg-slate-50 rounded-xl border text-xs">
                  <div>
                    <span className="text-slate-400 block">Code Generated:</span>
                    <span className="font-medium text-slate-700">
                      {new Date(selectedEntry.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Code Expires:</span>
                    <span className="font-medium text-slate-700">
                      {new Date(selectedEntry.expires_at).toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Postcard Received:</span>
                    <span className="font-medium text-slate-700">
                      {selectedEntry.postcard_received_at ? new Date(selectedEntry.postcard_received_at).toLocaleString() : "Pending receipt"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Reviewed At:</span>
                    <span className="font-medium text-slate-700">
                      {selectedEntry.reviewed_at ? new Date(selectedEntry.reviewed_at).toLocaleString() : "Not reviewed"}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block">Reviewed By:</span>
                    <span className="font-medium text-slate-700">
                      {selectedEntry.reviewer_name || (selectedEntry.reviewed_by ? `Admin #${selectedEntry.reviewed_by}` : "—")}
                    </span>
                  </div>
                  {selectedEntry.rejection_reason && (
                    <div className="col-span-2 sm:col-span-3 text-rose-600 bg-rose-50 p-2.5 rounded-lg border border-rose-200">
                      <span className="font-bold block">Rejection Reason:</span>
                      <span>{selectedEntry.rejection_reason}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Complete Audit History Timeline */}
              {selectedEntry.audit_logs && selectedEntry.audit_logs.length > 0 && (
                <div>
                  <h4 className="text-xs uppercase font-bold text-slate-500 tracking-wider mb-2 flex items-center gap-1.5">
                    <Clock size={14} /> Audit Trail & History Log
                  </h4>
                  <div className="space-y-2 border-l-2 border-slate-200 pl-4 ml-2">
                    {selectedEntry.audit_logs.map((log) => (
                      <div key={log.id} className="text-xs relative">
                        <div className="w-2.5 h-2.5 bg-indigo-500 rounded-full absolute -left-[21px] top-1" />
                        <div className="flex items-center justify-between text-slate-500">
                          <span className="font-bold text-slate-700 uppercase">
                            {log.action.replace(/_/g, " ")}
                          </span>
                          <span>{new Date(log.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-slate-600 mt-0.5">
                          {log.notes || `Changed status from ${log.previous_status || "initial"} to ${log.new_status}`}
                        </p>
                        {log.reason && (
                          <p className="text-rose-600 italic mt-0.5">Reason: {log.reason}</p>
                        )}
                        <span className="text-[10px] text-slate-400">
                          By: {log.performed_by_type} ({log.admin_username || log.performed_by_id || "System"})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Action Controls */}
            <div className="pt-4 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
              <button
                onClick={() => setSelectedEntry(null)}
                className="px-4 py-2 border rounded-xl text-slate-600 hover:bg-slate-50 text-xs font-semibold"
              >
                Close
              </button>

              <div className="flex flex-wrap items-center gap-2">
                {/* Mark Received */}
                {selectedEntry.status === "code_generated" && (
                  <button
                    onClick={() => handleUpdateStatus("postcard_received")}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold transition shadow-sm flex items-center gap-1.5"
                  >
                    <Mail size={14} />
                    Mark Postcard Received
                  </button>
                )}

                {/* Move to Under Review */}
                {selectedEntry.status === "postcard_received" && (
                  <button
                    onClick={() => handleUpdateStatus("under_review")}
                    disabled={actionLoading}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-xs font-bold transition shadow-sm flex items-center gap-1.5"
                  >
                    <Clock size={14} />
                    Move to Under Review
                  </button>
                )}

                {/* Actions when Received or Under Review */}
                {["postcard_received", "under_review"].includes(selectedEntry.status) && (
                  <>
                    <button
                      onClick={() => setShowRejectModal(true)}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 rounded-xl text-xs font-bold transition flex items-center gap-1.5"
                    >
                      <XCircle size={14} />
                      Reject Entry
                    </button>

                    <button
                      onClick={() => handleUpdateStatus("approved")}
                      disabled={actionLoading}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition shadow-sm flex items-center gap-1.5"
                    >
                      <CheckCircle size={14} />
                      Approve & Credit 2.00 SC
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── REJECTION REASON PROMPT MODAL ───────────────────────── */}
      {showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl relative border border-slate-200">
            <h3 className="text-lg font-bold text-rose-700 flex items-center gap-2 mb-2">
              <AlertTriangle size={20} />
              Reject AMOE Postcard Entry
            </h3>
            <p className="text-xs text-slate-500 mb-4">
              Select or provide a mandatory compliance reason for rejecting this entry. This explanation will be visible in the customer&apos;s history log.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Select Reason:
                </label>
                <select
                  value={rejectReasonSelect}
                  onChange={(e) => setRejectReasonSelect(e.target.value)}
                  className="w-full p-2.5 border rounded-xl text-xs bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                >
                  {REJECTION_REASONS.map((r, idx) => (
                    <option key={idx} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Custom or Additional Notes (Optional):
                </label>
                <textarea
                  rows={3}
                  value={customRejectReason}
                  onChange={(e) => setCustomRejectReason(e.target.value)}
                  placeholder="Specify additional details or observations..."
                  className="w-full p-2.5 border rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-rose-500/20"
                />
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowRejectModal(false)}
                className="px-4 py-2 border rounded-xl text-slate-600 hover:bg-slate-50 text-xs font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRejection}
                disabled={actionLoading}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
              >
                {actionLoading ? "Processing..." : "Confirm Rejection"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmoeManagement;
